<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import validations, { getLabelTitleErrorMessage } from './validations';
import { getRandomColor } from 'dashboard/helper/labelColor';
import { useVuelidate } from '@vuelidate/core';
import NextButton from 'dashboard/components-next/button/Button.vue';

export default {
  components: { NextButton },
  props: {
    prefillTitle: { type: String, default: '' },
  },
  emits: ['close'],
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      color: '#000',
      description: '',
      title: '',
      showOnSidebar: true,
    };
  },
  validations,
  computed: {
    ...mapGetters({ uiFlags: 'labels/getUIFlags' }),
    labelTitleErrorMessage() {
      return this.$t(getLabelTitleErrorMessage(this.v$));
    },
  },
  mounted() {
    this.color = getRandomColor();
    this.title = this.prefillTitle.toLowerCase();
  },
  methods: {
    onClose() { this.$emit('close'); },
    async addLabel() {
      try {
        await this.$store.dispatch('labels/create', {
          color: this.color,
          description: this.description,
          title: this.title.toLowerCase(),
          show_on_sidebar: this.showOnSidebar,
        });
        useAlert(this.$t('LABEL_MGMT.ADD.API.SUCCESS_MESSAGE'));
        this.onClose();
      } catch (error) {
        useAlert(error.message || this.$t('LABEL_MGMT.ADD.API.ERROR_MESSAGE'));
      }
    },
  },
};
</script>

<template>
  <div class="sz-label-modal">
    <woot-modal-header
      :header-title="$t('LABEL_MGMT.ADD.TITLE')"
      :header-content="$t('LABEL_MGMT.ADD.DESC')"
    />

    <form class="sz-label-form" @submit.prevent="addLabel">
      <!-- Name + Color row -->
      <div class="sz-label-row">
        <div class="sz-label-field sz-label-field-grow">
          <label class="sz-label-label"><span class="sz-required">*</span> Nome da etiqueta</label>
          <input
            v-model="title"
            type="text"
            class="sz-label-input"
            :class="{ 'sz-input-error': v$.title.$error }"
            placeholder="ex: suporte, vendas..."
            @input="v$.title.$touch"
            @blur="v$.title.$touch"
          />
          <span v-if="v$.title.$error" class="sz-error-text">{{ labelTitleErrorMessage }}</span>
        </div>
        <div class="sz-label-field sz-label-field-color">
          <label class="sz-label-label"><span class="sz-required">*</span> Cor</label>
          <woot-color-picker v-model="color" />
        </div>
      </div>

      <!-- Description -->
      <div class="sz-label-field">
        <label class="sz-label-label">Descrição</label>
        <input
          v-model="description"
          type="text"
          class="sz-label-input"
          placeholder="descrição da etiqueta"
        />
      </div>

      <!-- Show on sidebar -->
      <label class="sz-checkbox-wrap">
        <input v-model="showOnSidebar" type="checkbox" class="sz-checkbox" />
        <span>Exibir etiqueta na barra lateral</span>
      </label>

      <!-- Actions -->
      <div class="sz-label-actions">
        <button type="button" class="sz-btn-cancel" @click="onClose">Cancelar</button>
        <button
          type="submit"
          class="sz-btn-save"
          :disabled="v$.title.$invalid || uiFlags.isCreating"
        >
          {{ uiFlags.isCreating ? 'Salvando...' : 'Salvar' }}
        </button>
      </div>
    </form>
  </div>
</template>

<style scoped>
.sz-label-modal {
  padding: 24px;
  width: 100%;
  max-width: 480px;
  overflow: visible;
  box-sizing: border-box;
  background: #ffffff;
  border-radius: 12px;
}
.sz-label-form {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.sz-label-row {
  display: flex;
  gap: 12px;
  align-items: flex-end;
}
.sz-label-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.sz-label-field-grow { flex: 1; }
.sz-label-field-color {
  flex-shrink: 0;
  padding-bottom: 1px;
}
.sz-label-label {
  font-size: 13px;
  font-weight: 500;
  color: #363840;
}
.sz-required { color: #ef4444; }

.sz-label-input {
  height: 40px;
  padding: 0 12px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 14px;
  color: #111827;
  background: #ffffff;
  outline: none;
  width: 100%;
  box-sizing: border-box;
  text-transform: lowercase;
}
.sz-label-input:focus {
  border-color: #388E3C;
  box-shadow: 0 0 0 2px rgba(56,142,60,0.12);
}
.sz-label-input.sz-input-error { border-color: #ef4444; }
.sz-error-text { font-size: 12px; color: #ef4444; }

.sz-checkbox-wrap {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #363840;
  cursor: pointer;
}
.sz-checkbox {
  width: 18px;
  height: 18px;
  accent-color: #388E3C;
  cursor: pointer;
}

.sz-label-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 8px;
}
.sz-btn-cancel {
  height: 36px;
  padding: 0 16px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  background: #fff;
  color: #363840;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
}
.sz-btn-cancel:hover { background: #f9fafb; }
.sz-btn-save {
  height: 36px;
  padding: 0 20px;
  border: none;
  border-radius: 8px;
  background: #388E3C;
  color: #fff;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
}
.sz-btn-save:hover { background: #2E7D32; }
.sz-btn-save:disabled { opacity: 0.5; cursor: not-allowed; }

/* Dark mode */
.dark .sz-label-modal { background: #1E2023; }
.dark .sz-label-label { color: #d1d5db; }
.dark .sz-label-input { background: #1E2023; border-color: #363840; color: #f3f4f6; }
.dark .sz-label-input:focus { border-color: #4ADE80; box-shadow: 0 0 0 2px rgba(74,222,128,0.15); }
.dark .sz-checkbox-wrap { color: #d1d5db; }
.dark .sz-btn-cancel { background: #1E2023; border-color: #363840; color: #d1d5db; }
.dark .sz-btn-cancel:hover { background: #363840; }
</style>
